/**
 * Favoritos (dirección v2, DESIGN.md §5 y §11): buscador en la cabecera, barra de filtros única (biblioteca y materia),
 * la misma fila de documento de las bibliotecas y el documento en la ventana (§11.4). Solo datos reales de
 * GET /documents/favorites. Quitar usa POST /documents/:id/favorite (★ de la fila); guardar o quitar desde la ventana
 * llega por el evento `alertai:favorito`. Carpetas, notas personales y «Consultar con IA» del prototipo no tienen backend:
 * no se dibujan.
 */
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronRight, Star } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { abrirEnVentana } from '../components/documento/VentanaDocumento';
import { FOCO } from '../components/v3/Ajustes';
import { BarraFiltros, BotonFiltros, BuscadorCabecera, FichaFiltro, OpcionCapa, PieCapa, Segmentado, SeccionCapa } from '../components/biblioteca/BarraFiltros';
import FilaDocumento, { FilaCargando, ListaFilas, anchoDatos } from '../components/biblioteca/FilaDocumento';
import { copiarCita } from '../utils/filaDocumento';
import { useDensidad } from '../hooks/useDensidad';
import { EVENTO_FAVORITO, EventoFavorito } from '../hooks/useDocumento';
import { documentsService, FavoriteDocument } from '../services/documents.service';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { CollectionKey, collectionForDocument, getAllCollections } from '../utils/institutions';
import { guardado } from '../utils/fechas';
import { getSummaryText, parseAiSummary } from '../utils/formatters';
import { MATERIA_LABELS } from '../utils/constants';

const POR_PAGINA = 100;
const FILTRO_CORTO: Record<CollectionKey, string> = { adm: 'Administrativa', jud: 'TTA', ley: 'Leyes', conv: 'Convenios' };
const SIN_RESUMEN = 'Sin resumen disponible';

/** Renta, IVA y Otras primero, en el orden de la Biblioteca; las demás, alfabéticas. */
const ORDEN_MATERIA = ['renta', 'iva', 'otras'];
const ordenMateria = (m: string) => (ORDEN_MATERIA.includes(m) ? ORDEN_MATERIA.indexOf(m) : ORDEN_MATERIA.length);
const materiaLabel = (m: string) => (MATERIA_LABELS as Record<string, string>)[m.toLowerCase()] || m.charAt(0).toUpperCase() + m.slice(1);
/** Síntesis legible: el resumen guardado como JSON se limpia; si no hay, null. */
export function sintesis(doc: FavoriteDocument): string | null {
  if (!doc.resumen) return null;
  const texto = getSummaryText(parseAiSummary(null, doc.resumen), doc.resumen, doc.tipo);
  return texto && texto !== SIN_RESUMEN ? texto : null;
}
/** Coincidencia de la búsqueda: título, N°, materia o síntesis. */
const coincide = (doc: FavoriteDocument, q: string) =>
  !q || [doc.titulo, doc.numero, doc.materia && materiaLabel(doc.materia), sintesis(doc)].some(v => v && v.toLowerCase().includes(q));

export default function Favoritos() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const [items, setItems] = useState<FavoriteDocument[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState(false);
  const [cargandoMas, setCargandoMas] = useState(false);
  const [filtro, setFiltro] = useState<CollectionKey | 'todos'>('todos');
  const [materia, setMateria] = useState<string | null>(null);
  const [busqueda, setBusqueda] = useState('');

  // `offset` = filas ya cargadas: quitar favoritos achica la lista y una página por número saltaría filas.
  const cargar = useCallback(async (offset: number) => {
    const res = await documentsService.getFavorites(POR_PAGINA, offset);
    setTotal(res.pagination.total || 0);
    return res.data;
  }, []);

  const cargarPrimera = useCallback(async () => {
    setError(false);
    setItems(null);
    try {
      setItems(await cargar(0));
    } catch {
      setError(true);
    }
  }, [cargar]);

  // Depende del id y no del objeto: recargar solo si cambia el usuario.
  const userId = user?.id;
  const [densidad] = useDensidad(userId);
  useEffect(() => {
    if (userId) cargarPrimera();
  }, [userId, cargarPrimera]);

  // Quitar desde la ventana: la fila sale de la lista al volver; volver a guardarlo recarga la lista.
  useEffect(() => {
    const oir = (e: Event) => {
      const { id, favorito } = (e as CustomEvent<EventoFavorito>).detail;
      // Volver a guardarlo: se pide la primera página sin vaciar la lista (la ventana sigue encima y la lista no salta).
      if (favorito) {
        cargar(0).then(primera => setItems(prev => {
          const actuales = prev || [];
          const nuevos = primera.filter(d => !actuales.some(p => p.id === d.id));
          return [...nuevos, ...actuales];
        }), () => { /* sin red: la lista queda como estaba */ });
        return;
      }
      setItems(prev => (prev || []).filter(d => d.id !== id));
      setTotal(t => Math.max(0, t - 1));
    };
    window.addEventListener(EVENTO_FAVORITO, oir);
    return () => window.removeEventListener(EVENTO_FAVORITO, oir);
  }, [cargar]);

  const cargarMas = async () => {
    setCargandoMas(true);
    try {
      const mas = await cargar(items?.length || 0);
      setItems(prev => {
        const vistos = new Set((prev || []).map(d => d.id));
        return [...(prev || []), ...mas.filter(d => !vistos.has(d.id))];
      });
    } catch {
      toast.error('No se pudieron cargar más favoritos');
    } finally {
      setCargandoMas(false);
    }
  };

  const quitar = async (doc: FavoriteDocument) => {
    if (!user) return;
    const indice = (items || []).findIndex(d => d.id === doc.id);
    setItems(prev => (prev || []).filter(d => d.id !== doc.id));
    setTotal(t => Math.max(0, t - 1));
    try {
      // La ruta alterna: si el favorito ya se había quitado en otra pestaña, esta llamada lo vuelve a agregar y la
      // segunda lo deja quitado.
      let r = await documentsService.toggleFavorite(doc.id);
      if (r.isFavorite) r = await documentsService.toggleFavorite(doc.id);
      if (!r.success || r.isFavorite) throw new Error('sigue en favoritos');
      toast.success('Quitado de favoritos');
    } catch {
      // Vuelve solo este documento, a su lugar: otras quitas o «Cargar más» en curso no se deshacen.
      setItems(prev => {
        const lista = prev || [];
        if (lista.some(d => d.id === doc.id)) return lista;
        const copia = [...lista];
        copia.splice(Math.min(Math.max(indice, 0), copia.length), 0, doc);
        return copia;
      });
      setTotal(t => t + 1);
      toast.error('No se pudo quitar de favoritos');
    }
  };

  // Bibliotecas con al menos un favorito, en el orden fijo de las colecciones.
  const conteo = useMemo(() => {
    const c: Partial<Record<CollectionKey, number>> = {};
    (items || []).forEach(d => {
      const col = collectionForDocument(d.institution_code, d.tipo);
      if (col) c[col.key] = (c[col.key] || 0) + 1;
    });
    return c;
  }, [items]);
  const materias = useMemo(
    () => Array.from(new Set((items || []).map(d => d.materia?.toLowerCase()).filter((m): m is string => !!m))).sort((a, b) => ordenMateria(a) - ordenMateria(b) || materiaLabel(a).localeCompare(materiaLabel(b), 'es')),
    [items],
  );
  const bibliotecas = getAllCollections().filter(c => conteo[c.key]);
  const filtroVigente = filtro !== 'todos' && !conteo[filtro] ? 'todos' : filtro;
  const materiaVigente = materia && materias.includes(materia) ? materia : null;
  const q = busqueda.trim().toLowerCase();
  const visibles = (items || []).filter(d =>
    (filtroVigente === 'todos' || collectionForDocument(d.institution_code, d.tipo)?.key === filtroVigente) &&
    (!materiaVigente || d.materia?.toLowerCase() === materiaVigente) &&
    coincide(d, q));
  const hayFiltros = filtroVigente !== 'todos' || !!materiaVigente || !!q;
  const limpiar = () => { setFiltro('todos'); setMateria(null); setBusqueda(''); };
  // Abrir un favorito: la ventana de documento (DESIGN.md §11.4) sobre esta lista; ↑ ↓ recorren los visibles.
  const abrir = (doc: FavoriteDocument) => navigate(`/documento/${doc.id}`, abrirEnVentana(location, visibles.map(d => d.id), q));
  // Lista mixta: una sola columna de datos (la más ancha presente) para que las acciones queden alineadas.
  const anchoColumna = Math.max(150, ...visibles.map(d => anchoDatos(collectionForDocument(d.institution_code, d.tipo)?.key || 'adm')));
  const copiar = async (doc: FavoriteDocument) => {
    try { await copiarCita(doc); toast.success('Cita copiada'); } catch { toast.error('No se pudo copiar la cita'); }
  };

  const [capa, setCapa] = useState(false);
  const cerrarCapa = useCallback(() => setCapa(false), []);
  const buscador = useRef<HTMLInputElement>(null);
  const conteoTexto = error ? '' : items === null
    ? 'Cargando…'
    : hayFiltros ? `${visibles.length.toLocaleString('es-CL')} de ${items.length.toLocaleString('es-CL')}` : `${total.toLocaleString('es-CL')} guardado${total === 1 ? '' : 's'}`;

  // El título va una sola vez, en la cabecera, con el buscador al lado (DESIGN.md §11.1; dueño 19-10).
  const buscadorCabecera = items !== null && items.length > 0 ? (
    <BuscadorCabecera
      entrada={buscador}
      valor={busqueda}
      onChange={setBusqueda}
      onBuscar={(e?: FormEvent) => e?.preventDefault()}
      onBorrar={() => setBusqueda('')}
      placeholder="Buscar en tus favoritos por título, número o materia"
      etiqueta="Buscar en tus favoritos"
    />
  ) : undefined;

  return (
    <Layout title="Favoritos" search={buscadorCabecera}>
      <div className="-m-4 lg:-m-8 min-h-[calc(100vh-85px)] bg-white">
        <div className="px-4 lg:px-8 pb-7">
        <div className="w-full max-w-[1000px]">
          <h1 className="sr-only">Favoritos</h1>

          {error && (
            <p className="pt-6 text-[13px] text-slate-500" role="alert">
              No se pudieron cargar tus favoritos. Revisa tu conexión e inténtalo de nuevo.{' '}
              <button type="button" onClick={cargarPrimera} className={`font-medium text-[#4F46E5] hover:underline rounded ${FOCO}`}>Reintentar</button>
            </p>
          )}

          {!error && items !== null && items.length === 0 && (
            <div className="mt-6 rounded-2xl border border-slate-200/85 bg-white py-12 px-4 text-center space-y-3">
              <span className="w-12 h-12 mx-auto rounded-full bg-[#EEF2FF] text-[#4F46E5] flex items-center justify-center" aria-hidden="true">
                <Star className="w-5 h-5" />
              </span>
              <p className="text-[13.5px] font-medium text-slate-900">Aún no guardas documentos</p>
              <p className="text-[13.5px] text-slate-600">Marca con ★ un documento en una biblioteca o en su ficha y aparecerá aquí.</p>
              <Link to="/bibliotecas" className={`inline-flex items-center gap-0.5 px-3 py-1.5 rounded-lg text-[12px] font-medium bg-[#EEF2FF] text-[#4F46E5] ${FOCO}`}>
                Ir a Bibliotecas <ChevronRight className="w-3.5 h-3.5" aria-hidden="true" />
              </Link>
            </div>
          )}

          {!error && (items === null || items.length > 0) && (
            <>
              {/* Barra de filtros única (§11.3): biblioteca, «Filtros» (materia), fichas y conteo */}
              <BarraFiltros derecha={<span className="text-slate-500 tabular-nums" aria-live="polite">{conteoTexto}</span>}>
                {bibliotecas.length > 1 && (
                  <Segmentado
                    etiqueta="Filtrar por biblioteca"
                    valor={filtroVigente}
                    onChange={setFiltro}
                    opciones={[{ valor: 'todos' as const, texto: 'Todos' }, ...bibliotecas.map(c => ({ valor: c.key, texto: FILTRO_CORTO[c.key] }))]}
                  />
                )}
                {materias.length > 1 && (
                  <BotonFiltros activos={materiaVigente ? 1 : 0} abierta={capa} onAbrir={() => setCapa(true)} onCerrar={cerrarCapa}>
                    <SeccionCapa titulo="Materia">
                      <div className="flex flex-wrap gap-1.5">
                        {materias.map(m => (
                          <OpcionCapa key={m} activa={materiaVigente === m} onClick={() => setMateria(materiaVigente === m ? null : m)}>{materiaLabel(m)}</OpcionCapa>
                        ))}
                      </div>
                    </SeccionCapa>
                    <PieCapa onLimpiar={() => setMateria(null)} onListo={cerrarCapa} />
                  </BotonFiltros>
                )}
                {materiaVigente && <FichaFiltro texto={materiaLabel(materiaVigente)} onQuitar={() => setMateria(null)} />}
              </BarraFiltros>

              {/* Lista: la misma fila de documento de las bibliotecas (DESIGN.md §5 y §11.2); el documento se abre en la ventana */}
              <div>
                {items === null && (
                  <div className="border-t border-slate-100">
                    {Array.from({ length: 6 }, (_, i) => <FilaCargando key={i} i={i} coleccion="adm" compacta={densidad === 'compacta'} />)}
                  </div>
                )}

                {items !== null && visibles.length === 0 && (
                  <p className="py-5 text-[13px] text-slate-500" role="status">
                    Ningún favorito coincide con estos filtros.{' '}
                    <button type="button" onClick={limpiar} className={`font-medium text-[#4F46E5] hover:underline rounded ${FOCO}`}>Mostrar todos los favoritos</button>
                  </p>
                )}

                {items !== null && visibles.length > 0 && (
                  <ListaFilas docs={visibles} agrupacion={null} fila={doc => (
                    <FilaDocumento
                      key={doc.id}
                      doc={doc}
                      coleccion={collectionForDocument(doc.institution_code, doc.tipo)?.key || 'adm'}
                      href={`/documento/${doc.id}`}
                      consulta={q}
                      compacta={densidad === 'compacta'}
                      anchoColumna={anchoColumna}
                      favorito
                      notaColumna={guardado(doc.favorited_at)}
                      onAbrir={() => abrir(doc)}
                      onFavorito={() => quitar(doc)}
                      onCopiar={() => copiar(doc)}
                    />
                  )} />
                )}
              </div>

              {items !== null && items.length < total && (
                <div className="flex flex-wrap items-center gap-3">
                  <button type="button" onClick={cargarMas} disabled={cargandoMas} className={`px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-[12px] font-medium ${FOCO} ${cargandoMas ? 'text-slate-400 cursor-not-allowed' : 'text-slate-700 hover:bg-slate-50'}`}>
                    {cargandoMas ? 'Cargando…' : `Cargar más (${(total - items.length).toLocaleString('es-CL')} restantes)`}
                  </button>
                  <span className="text-xs text-slate-500">El filtro se aplica a los {items.length.toLocaleString('es-CL')} ya cargados.</span>
                </div>
              )}
            </>
          )}
        </div>
        </div>
      </div>

    </Layout>
  );
}
